// The `/help` subpath, not the package root: the root pulls in server-only
// modules (see `content/ContentProvider.tsx`). This entry compiles to data.
import { HELP_FALLBACKS, type HelpContent, helpContent } from '@jt/platform-content/help';

import { apiGet } from '../../session/client';
import { type Appointment, type Listing, getListings, listAppointments } from '../bookings/bookings-api';
import { type Order, listOrders } from '../orders/orders-api';

/**
 * Everything the Help page reads, and nothing it invents.
 *
 * - `content/help` — the topics and answers as the owner last published them
 *   in the dashboard. Public. If it cannot be read the page draws the copy it
 *   ships with, which is the same declaration the dashboard edits.
 * - `consumer/orders` — this browser's guest orders, plus the signed-in
 *   person's own (the server decides whose; no person id is sent).
 * - `me/bookings` — the signed-in person's appointments, with the listing
 *   behind the few offered, for its title and shop.
 */

export type { HelpContent, HelpQuestion, HelpTopic, HelpTopicId } from '@jt/platform-content/help';

/** The copy the page ships with: what it shows before, or without, the API. */
export const SHIPPED_HELP: HelpContent = helpContent(HELP_FALLBACKS);

export const getHelp = (): Promise<HelpContent> => apiGet<HelpContent>('/content/help');

/** Most recent first, as the route answers; the page shows the first few. */
export const recentOrders = (): Promise<readonly Order[]> => listOrders();

/**
 * The signed-in person's appointments worth offering — the next ones still to
 * come, else the latest — with the listing behind each for its title and shop.
 * Every page is read (the capped-list trap), and only the chosen few listings.
 */
export async function recentAppointments(
  pick: (items: readonly Appointment[]) => readonly Appointment[],
): Promise<readonly { readonly appointment: Appointment; readonly listing?: Listing }[]> {
  const { items } = await listAppointments();
  const chosen = pick(items);
  const listings = await getListings(chosen.map((item) => item.listingId)).catch(
    () => new Map<string, Listing>(),
  );
  return chosen.map((appointment) => {
    const listing = listings.get(appointment.listingId);
    return listing === undefined ? { appointment } : { appointment, listing };
  });
}
